// src/pages/Login.jsx
import React, { useEffect, useState } from "react";
import { auth, db } from "../services/firebase";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate, useLocation, Link } from "react-router-dom";

const provider = new GoogleAuthProvider();
provider.setCustomParameters({ prompt: "select_account" });

async function ensureRoleDoc(uid) {
  const ref = doc(db, "roles", uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, { createdAt: serverTimestamp() }, { merge: true });
  }
}

async function saveUserProfile(user) {
  if (!user) return;
  const ref = doc(db, "users", user.uid);
  await setDoc(
    ref,
    {
      uid: user.uid,
      email: user.email || null,
      displayName: user.displayName || null,
      photoURL: user.photoURL || null,
      providerId: (user.providerData[0] && user.providerData[0].providerId) || "password",
      lastLoginAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

function humanizeError(e) {
  const code = (e?.code || "").toLowerCase();
  if (code.includes("network-request-failed")) return "Problème réseau. Vérifiez votre connexion.";
  if (code.includes("popup-closed-by-user")) return "Fenêtre fermée avant la fin.";
  if (code.includes("invalid-email")) return "Email invalide.";
  if (code.includes("user-not-found")) return "Utilisateur introuvable.";
  if (code.includes("wrong-password")) return "Mot de passe incorrect.";
  if (code.includes("email-already-in-use")) return "Cet email est déjà utilisé.";
  if (code.includes("too-many-requests")) return "Trop de tentatives. Réessayez plus tard.";
  return e?.message || "Erreur inconnue.";
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state && location.state.from) || "/";

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) {
        // déjà connecté -> redirige
        navigate(redirectTo, { replace: true });
      }
    });
    return () => unsub();
  }, [navigate, redirectTo]);

  async function loginGoogle() {
    setMsg("");
    setLoading(true);
    try {
      const res = await signInWithPopup(auth, provider);
      await ensureRoleDoc(res.user.uid);
      await saveUserProfile(res.user);
      setMsg("Connecté avec Google ✅");
      navigate(redirectTo, { replace: true });
    } catch (e) {
      setMsg(humanizeError(e));
    } finally {
      setLoading(false);
    }
  }

  async function loginEmail(e) {
    e.preventDefault();
    setMsg("");
    setLoading(true);
    try {
      if (mode === "register") {
        const res = await createUserWithEmailAndPassword(auth, email, pass);
        // Option : définir un displayName basique si absent
        if (!res.user.displayName) {
          try { await updateProfile(res.user, { displayName: email.split("@")[0] }); } catch {}
        }
        await ensureRoleDoc(res.user.uid);
        await saveUserProfile(res.user);
        setMsg("Compte créé et connecté ✅");
      } else {
        const res = await signInWithEmailAndPassword(auth, email, pass);
        await saveUserProfile(res.user);
        setMsg("Connecté ✅");
      }
      navigate(redirectTo, { replace: true });
    } catch (e) {
      setMsg(humanizeError(e));
    } finally {
      setLoading(false);
    }
  }

  async function doReset(e) {
    e.preventDefault();
    setMsg("");
    if (!email) return setMsg("Entrez d'abord votre email.");
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setMsg("Email de réinitialisation envoyé ✅");
      setResetMode(false);
    } catch (e) {
      setMsg(humanizeError(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container py-4" style={{ maxWidth: 520 }}>
      <h3 className="mb-3">Connexion</h3>

      {/* Connexion Google */}
      <div className="card p-3 mb-3">
        <button className="btn btn-outline-dark w-100" onClick={loginGoogle} disabled={loading}>
          {loading ? "Veuillez patienter..." : "Continuer avec Google"}
        </button>
      </div>

      {/* Email / Password */}
      <div className="card p-3">
        {!resetMode ? (
          <form onSubmit={loginEmail} className="row g-2">
            <div className="col-12">
              <input
                className="form-control"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
            <div className="col-12">
              <input
                className="form-control"
                type="password"
                placeholder="Mot de passe"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                required
              />
            </div>

            <div className="col-12 d-flex align-items-center justify-content-between">
              <div className="form-text">
                {mode === "register"
                  ? "Mot de passe conseillé : 8+ caractères."
                  : "Gardez vos accès confidentiels."}
              </div>
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => setResetMode(true)}
                disabled={loading}
              >
                Mot de passe oublié ?
              </button>
            </div>

            <div className="col-12 d-flex gap-2">
              <button className="btn btn-dark" type="submit" disabled={loading}>
                {loading ? "Chargement..." : mode === "register" ? "Créer le compte" : "Se connecter"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setMode((m) => (m === "login" ? "register" : "login"))}
                disabled={loading}
              >
                {mode === "register" ? "J'ai déjà un compte" : "Créer un compte"}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={doReset} className="row g-2">
            <div className="col-12">
              <input
                className="form-control"
                type="email"
                placeholder="Votre email pour réinitialiser"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
            <div className="col-12 d-flex gap-2">
              <button className="btn btn-dark" type="submit" disabled={loading}>
                {loading ? "Envoi..." : "Envoyer le lien"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setResetMode(false)}
                disabled={loading}
              >
                Annuler
              </button>
            </div>
          </form>
        )}
      </div>

      {msg && <div className="alert alert-info mt-3 py-2">{msg}</div>}

      {/* Raccourcis utiles */}
      <div className="mt-3 d-flex justify-content-between align-items-center">
        <button className="btn btn-link text-danger p-0" onClick={() => signOut(auth)} disabled={loading}>
          Se déconnecter
        </button>
        <Link to="/" className="btn btn-link p-0">Retour à l’accueil</Link>
      </div>
    </div>
  );
}
